import React, { useState, useEffect } from 'react'
import { graphql, Link } from 'gatsby'
import tw, { styled } from 'twin.macro'
import capitalize from 'capitalize'
import { ReactSearchAutocomplete } from 'react-search-autocomplete'
import { Helmet } from 'react-helmet';

// Components
import Layout from '../components/Layout'
import Icons from '../components/Icons';
import {
  ButtonStyle,
  CopySMStyle,
  CopyStyle,
  HeaderStyle,
  SubheadSMStyle,
  SubheadStyle,
} from '../components/UI'
import CallToAction from '../components/CallToAction'
import ReactPaginate from 'react-paginate'
import { Listbox } from '@headlessui/react'
import SliderUnstyled, { sliderUnstyledClasses } from '@mui/base/SliderUnstyled'
import { GatsbyImage } from 'gatsby-plugin-image'
import Seo from '../components/Seo'
import '../styles/job.css';
import JobFilters from '../components/JobFilters'
import { transformJobTitle } from '../utils'
import { useQueryParam } from 'use-query-params'


const Jobs = ({ location, pageContext, data }) => {
  const pageData = data.contentfulJobsPage
  const jobs = pageContext.jobs
  const [industry] = useQueryParam('industry');

  const locations = []
  jobs.forEach((job) => {
    const city = job[1].usa_city || job[1].canada_city;
    if (!locations.includes(city) && (city !== undefined || city !== ''))
      locations.push(city)
  })
  locations.sort((a, b) => a.localeCompare(b))

  const [selectedLocation, setSelectedLocation] = useState('Select Location')
  const [rateSliderValue, setRateSliderValue] = useState(20)
  const [order, setOrder] = useState('newest')
  const [orderLabel, setOrderLabel] = useState('Newest')
  const [filterResults, setFilterResults] = useState([])
  const [pageNumber, setPageNumber] = useState(0)
  const [skills, setSkills] = useState([])
  const [searchString, setSearchString] = useState('')

  useEffect(() => {
    updateResults()
  }, [rateSliderValue, order])

  useEffect(() => {
    updateResults()
  }, [])

  const filterKeyword = (jobKeyword, keyword) => {
    if (
      jobKeyword &&
      jobKeyword.toLowerCase().includes(keyword.toLowerCase())
    ) {
      return true
    } else {
      return false
    }
  }

  const filterLocation = (jobLocation, location) => {
    return jobLocation
      ? jobLocation.toLowerCase().includes(location.toLowerCase())
      : false
  }

  const filterRate = (jobRate, rate) => {
    if (jobRate && Number(jobRate) >= Number(rate)) {
      return true
    } else {
      return false
    }
  }

  const updateResults = () => {
    // Filter by keyword, location, and rate
    const filtered = jobs.filter((job) => {
      if (
        searchString === ''
          ? true
          : filterKeyword(job[1].title, searchString)
        // ||
        // filterKeyword(job[1].description, searchString)
      ) {
        if (
          selectedLocation === 'Select Location'
            ? true
            : filterLocation(job[1].location, selectedLocation)
        ) {
          if (filterRate(job[1].salary_min, rateSliderValue)) {
            if (!industry || job[1].industry === industry) {
              return true
            }
          }
        }
      } else {
        return false
      }
    })
    if (searchString && selectedLocation && selectedLocation !== 'Select Location') {
      window.fbq('track', 'Search', { jobTitle: searchString, location: selectedLocation })
    }

    // Sort
    if (order === 'newest') {
      setOrderLabel('Newest')
      filtered.sort((first, next) => {
        return new Date(next[1].date) - new Date(first[1].date)
      })
    } else if (order === 'rateHighToLow') {
      setOrderLabel('Pay rate highest to lowest')
      filtered.sort((first, next) => {
        return next[1].salary_min - first[1].salary_min
      })
    } else if (order === 'rateLowToHigh') {
      setOrderLabel('Rate Low To High')
      filtered.sort((first, next) => {
        return first[1].salary_min - next[1].salary_min
      })
    }

    setSkills(() => {
      const uniqueSkills = [...new Set(jobs.map((job) => job[1].title))];
      return uniqueSkills.map((skill, index) => ({ id: index, name: skill }));
    });

    // Set filtered results usestate
    setFilterResults(filtered)

    // Set pagination page
    setPageNumber(0)
  }

  const formatResult = (item) => {
    return (
      <>
        <span key={`sklll-${item.id}`}>{item.name}</span>
      </>
    )
  }

  const handleOnSelect = (item) => {
    setSearchString(item.name)
    updateResults()
  }

  const handleOnClear = () => {
    setSearchString('')
    updateResults()
  }

  const handleOnSearch = (string, results) => {
    // onSearch will have as the first callback parameter
    // the string searched and for the second the results.
    setSearchString(string)
    updateResults()
  }
  return (
    <Layout location={location}>
      <Helmet>
        {/* Facebook SDK script */}
        <script>
          {` !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '2071187066580648');
            fbq('track', 'Search');`}
        </script>

        {/* noscript section */}
        {/* <script
          src={`https://www.facebook.com/tr?id=2071187066580648&ev=PageView&noscript=1`}
        ></script> */}
      </Helmet>
      <Seo
        title={pageData.metadataTitle || 'Job Board'}
        description={pageData.metadataDescription}
        image={
          pageData.metadataImage
            ? `https:${pageData.metadataImage.file.url}`
            : undefined
        }
      />
      {/* Hero */}
      <section tw="font-mont bg-[#F1FAFD]">
        <div tw="flex flex-row h-[500px]">
          {/* Image */}
          {/* <div tw="order-1 h-48 relative overflow-x-hidden lg:(order-2 col-span-12 h-full)"> */}
          <GatsbyImage
            tw="w-full filter brightness-[60%] h-full"
            image={pageData.heroImage.gatsbyImageData}
            alt={pageData.heroImage.filename}
            imgClassName="height: 130%"
          />
          {/* </div> */}
          {/* Content */}
          {/* <div tw="order-2 mx-7 my-12 lg:(order-1 flex flex-col justify-center mx-0 mb-0 col-start-2 col-span-5 my-0)"> */}
          <div id="job-hero-headline">
            <h1 tw='text-white text-center text-[30px] mt-[25px] font-extrabold lg:(mt-[125px] text-[40px])'>{pageData.heroHeadline}</h1>
            <p tw='text-white text-[18px] lg:(ml-[25%] w-[50%]) lg:(text-[25px])'>{pageData.heroSubhead}</p>

          </div>
          <div tw='absolute top-[300px] left-[15%] w-[70%] lg:(top-[380px])'>
            <div tw="col-span-4 lg:(col-span-full)">
              <form
                css={[CopyStyle, tw`lg:(flex items-center)`]}
                onSubmit={(e) => {
                  e.preventDefault()
                  updateResults()
                }}
              >
                {/* Text input */}
                {/* <div> */}
                {/* <input
                  css={[
                    CopyStyle,
                    tw`border border-black border-b-0 w-full p-7 lg:(flex-1 border-b border-r-0)`,
                  ]}
                  type="text"
                  id="Keyword"
                  name="Keyword"
                  placeholder="Keyword"
                  onChange={(e) => {
                    console.log('on change', e)
                    setKeyword(e.target.value)
                  }}
                />
                <div className='matched-items-container'>
                  <p>{'g'}</p>
                </div> */}

                {/* </div> */}
                {/* Dropdown */}

                <div className='skill-auto-complete-container' tw='w-[100%] lg:(w-[50%])'>
                  <ReactSearchAutocomplete
                    items={skills}
                    onSelect={handleOnSelect}
                    formatResult={formatResult}
                    onSearch={handleOnSearch}
                    autoFocus={true}
                    placeholder="Search Skills"
                    styling={{ placeholderColor: 'black' }}
                    fuseOptions={{ minMatchCharLength: 3, isCaseSensitive: false }}
                    onClear={handleOnClear}
                  />
                </div>
                {locations && (
                  <Listbox
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    as="div"
                    tw="relative w-full lg:(w-4/12)"
                  >
                    {({ open }) => (
                      <>
                        <Listbox.Button tw="border border-black bg-white w-full p-7 text-left flex justify-between items-center">
                          <span>{selectedLocation}</span>
                          <svg
                            width="12"
                            height="7"
                            viewBox="0 0 12 7"
                            fill="none"
                            css={[
                              tw`text-[#FF4D00] stroke-current w-3`,
                              open && tw`rotate-180`,
                            ]}
                          >
                            <path
                              d="M1 1L6 6L11 1"
                              stroke=""
                              strokeWidth="1.04499"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Listbox.Button>
                        <Listbox.Options tw="border border-black bg-white w-full absolute top-full border-t-0 h-[170px] overflow-y-scroll">
                          <Listbox.Option
                            key={`location-all`}
                            value="Select Location"
                          >
                            {({ active, selected }) => (
                              <div
                                css={[
                                  tw`px-7 py-5 cursor-pointer`,
                                  active && tw`bg-[#E5EEFF]`,
                                ]}
                              >
                                Select Location
                              </div>
                            )}
                          </Listbox.Option>
                          {locations.map((location) => {
                            return (
                              <Listbox.Option
                                key={`location-${location}`}
                                value={location}
                              >
                                {({ active, selected }) => (
                                  <div
                                    css={[
                                      tw`px-7 py-5 cursor-pointer`,
                                      active && tw`bg-[#E5EEFF]`,
                                    ]}
                                  >
                                    {location}
                                  </div>
                                )}
                              </Listbox.Option>
                            )
                          })}
                        </Listbox.Options>
                      </>
                    )}
                  </Listbox>
                )}
                {/* Button */}
                <input
                  css={[
                    ButtonStyle,
                    tw`p-7 bg-[#FF4D00] text-white border-[3px] border-[#FF4D00] w-full cursor-pointer lg:(w-2/12)`,
                  ]}
                  type="submit"
                  value="Search"
                />
              </form>
            </div>
          </div>
        </div>
      </section>

      <section tw="grid grid-cols-12 pt-14 pb-28 lg:(max-w-screen-lg mx-auto pt-16 pb-32)">
        {/* Job Filters */}
        <JobFilters
          orderLabel={orderLabel}
          setOrder={setOrder}
          rateSliderValue={rateSliderValue}
          setRateSliderValue={setRateSliderValue}
        />
        {/* Paginated job results */}
        <div tw="px-[15px] col-span-12 lg:(px-[0])">
          <div css={[SubheadStyle]}>
            {/* <span>{keyword}</span> */}
            <span css={[CopyStyle, tw`inline-block mt-8 mb-2`]}>{`${filterResults.length > 9 ? 9 : filterResults.length
              } of ${filterResults.length} results`}</span>
          </div>
          <PaginatedItems
            allJobs={filterResults}
            itemsPerPage={9}
            page={pageNumber}
            setPage={setPageNumber}
          />
        </div>
      </section>

      {/* CTA */}
      <CallToAction
        header={pageData.ctaHeadline}
        copy={pageData.ctaSubhead.ctaSubhead}
        ctas={[
          { copy: 'Start Earning', destination: '/start-earning' },
          { copy: 'Find Talent', destination: '/find-talent' },
        ]}
        image={pageData.ctaImage}
      />
    </Layout>
  )
}

export default Jobs

export const pageQuery = graphql`
  {
    contentfulJobsPage(pageId: { eq: "Jobs Page" }) {
      heroHeadline
      heroSubhead
      heroImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      ctaHeadline
      ctaSubhead {
        ctaSubhead
      }
      ctaImage {
        filename
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          width: 1024
          quality: 80
        )
      }
      metadataTitle
      metadataDescription
      metadataImage {
        file {
          url
        }
      }
    }
  }
`

function Items({ currentItems }) {
  return (
    <div>
      {currentItems &&
        currentItems.map((job, index) => (
          <Link
            key={`job-${index}`}
            to={`/jobs/${job[1].country.toLowerCase()}/${(
              job[1].canada_province || job[1].usa_state
            )
              .toLowerCase()
              .replace(' ', '-')}/1/${job[1].id}`}
          >
            <div tw="border border-black w-full py-7 px-8 mb-4 lg:(py-8 px-12)">
              <div tw="flex justify-between grid grid-cols-1 lg:(grid-cols-2)">
                <div>
                  <h2 tw="font-mont font-bold text-[18px] inline-block lg:(text-[24px])">
                    {transformJobTitle(job[1].title)}
                  </h2>
                  <span tw="ml-1 text-[12px] font-light ml-[8px] lg:(text-[18px])">
                    {job[1].canada_city && (
                      <span>{`${job[1].canada_city}, ${job[1].canada_province}`}</span>
                    )}
                    {job[1].usa_city && (
                      <span>{`${job[1].usa_city}, ${job[1].usa_state}`}</span>
                    )}
                  </span>
                </div>
                <div tw="mt-1 lg:(text-right)">
                  <span css={[CopyStyle]}>
                    <span tw="font-bold text-[20px] lg:(text-[30px])">
                      {`$${job[1]?.applyFormId ? job[1].salary_min + '+' : job[1].salary_min} ${job[1].currency.toUpperCase()}`}
                    </span>
                    <span tw="font-light text-[18px] lg:(text-[24px])">
                      /{' '}
                      {`${job[1].pay_duration === 'Hourly'
                        ? 'hour'
                        : job[1].pay_duration.toLowerCase()
                        }`}
                    </span>
                  </span>
                </div>
              </div>
              <p tw="mt-5 font-mont">
                {job[1].description.replace(/(<([^>]+)>)/gi, '').split('.')[0]}{' '}
                <a href={`job-${index}`} tw="text-[#FF4D00] underline">
                  see details
                </a>
              </p>
              <p tw="mt-5 text-right font-mont">
                {/* <a href={`job-${index}`} tw="text-[#FF4D00] underline"> */}
                {/* <div class="btn btn_red"><span class="icon"></span><a href="#">Crimson</a><span></span></div> */}

                <a
                  href={`job-${index}`}
                  tw="cursor-pointer bg-[#FF4D00] text-white rounded-[30px] pt-[24px] pr-[1px] pb-[1px] pl-[11px] lg:(pt-[40px] pr-[5px] pb-[3px] pl-[20px])"
                >
                  <span tw="font-mont relative bottom-[12px] text-[12px] font-bold lg:(text-[16px] bottom-[16px])">
                    {' '}
                    See details
                  </span>
                  <Icons
                    icon="rightArrow"
                    tw="bg-white text-[#FF4D00] rounded-[50%] py-[10px] px-[12px] ml-[20px] lg:(py-[17px] px-[20px] ml-[30px])"
                  />
                </a>
                {/* </a> */}
              </p>
            </div>
          </Link>
        ))}
    </div>
  )
}

export function PaginatedItems({ allJobs, itemsPerPage, page, setPage }) {
  // We start with an empty list of items.
  const [currentItems, setCurrentItems] = useState(null)
  const [pageCount, setPageCount] = useState(0)
  // Here we use item offsets; we could also use page offsets
  // following the API or data you're working with.
  const [itemOffset, setItemOffset] = useState(0)

  useEffect(() => {
    const newOffset = (page * itemsPerPage) % allJobs.length
    setItemOffset(newOffset)
    // Fetch items from another resources.
    const endOffset = itemOffset + itemsPerPage
    // console.log(`Loading items from ${itemOffset} to ${endOffset}`)
    setCurrentItems(allJobs.slice(itemOffset, endOffset))
    setPageCount(Math.ceil(allJobs.length / itemsPerPage))
  }, [itemOffset, itemsPerPage, allJobs, page])

  // Invoke when user click to request another page.
  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % allJobs.length
    // console.log(`User requested page number ${event.selected}, which is offset ${newOffset}`)
    setItemOffset(newOffset)
    setPage(event.selected)
  }

  return (
    <>
      <Items currentItems={currentItems} />
      <ReactPaginate
        forcePage={page}
        breakLabel="..."
        nextLabel=">"
        onPageChange={handlePageClick}
        pageRangeDisplayed={5}
        pageCount={pageCount}
        previousLabel="<"
        renderOnZeroPageCount={null}
        css={[
          CopyStyle,
          tw`w-full mt-10 flex justify-center items-center lg:mt-16`,
        ]}
        nextClassName="pagination-next"
        previousClassName="pagination-previous"
        activeClassName="pagination-active"
        disabledClassName="pagination-disabled"
        pageClassName="pagination-page"
      />
    </>
  )
}

export const StyledSlider = styled(SliderUnstyled)(
  ({ theme }) => `
  color: #1976d2;
  height: 4px;
  width: 100%;
  padding: 13px 0;
  display: inline-block;
  position: relative;
  cursor: pointer;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    opacity: 1;
  }

  &.${sliderUnstyledClasses.disabled} { 
    pointer-events: none;
    cursor: default;
    color: #bdbdbd; 
  }

  & .${sliderUnstyledClasses.rail} {
    display: block;
    position: absolute;
    top: 15px;
    width: 100%;
    height: 1px;
    border-radius: 2px;
    background-color: silver;
  }

  & .${sliderUnstyledClasses.track} {
    display: block;
    position: absolute;
    height: 4px;
    border-radius: 2px;
    background-color: #FF4D00;
  }

  & .${sliderUnstyledClasses.thumb} {
    position: absolute;
    width: 14px;
    height: 14px;
    margin-left: -6px;
    margin-top: -5px;
    box-sizing: border-box;
    border-radius: 50%;
    outline: 0;
    border: 2px solid black;
    background-color: black;
  }
`
)
